import { useState } from 'react';
import { CreditCard, Download, ExternalLink, Plus, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { downloadCRMExport } from '@api/crm';
import { EditSubscriptionOverridesDrawer } from '@components/accounts/EditSubscriptionOverridesDrawer';
import { EmptyState } from '@components/common/EmptyState';
import { ModalPortal } from '@components/common/ModalPortal';
import { PageHeader } from '@components/common/PageHeader';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { SectionCard } from '@components/common/SectionCard';
import { useAccounts } from '@hooks/useCRM';
import { useTenantSubscription } from '@hooks/useTenantSubscription';
import { getApolloAccountUrls } from '@lib/apollo';
import { formatRelativeDate } from '@lib/format';

const ACCOUNT_STATUS_OPTIONS = [
  { id: '', label: 'All statuses' },
  { id: 'lead', label: 'Lead' },
  { id: 'prospect', label: 'Prospect' },
  { id: 'customer', label: 'Customer' },
  { id: 'inactive', label: 'Inactive' },
];

export function AccountsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [editingSubscriptionAccountId, setEditingSubscriptionAccountId] = useState<string | null>(null);
  const { data, isLoading } = useAccounts(search || undefined, status || undefined);
  const subscription = useTenantSubscription(editingSubscriptionAccountId ?? undefined);

  return (
    <div className="space-y-8">
      <PageHeader
        title="Accounts"
        subtitle="Companies tracked across lead generation, sales, and customer transition."
      />

      <SectionCard
        title="Account List"
        action={(
          <div className="toolbar-cluster">
            <label className="search-field">
              <Search size={16} />
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search accounts" />
            </label>
            <SearchableSelect
              options={ACCOUNT_STATUS_OPTIONS}
              value={status}
              onChange={setStatus}
              placeholder="All statuses"
              clearable={false}
              aria-label="Account status"
            />
            <button type="button" className="button button-secondary" onClick={() => navigate('/accounts/new')}>
              <Plus size={16} />
              New Account
            </button>
            <button type="button" className="button button-secondary" onClick={() => downloadCRMExport('accounts', 'csv')}>
              <Download size={16} />
              Export CSV
            </button>
          </div>
        )}
      >
      {isLoading ? (
        <div className="loading-row">Loading accounts…</div>
      ) : data?.items.length ? (
        <div className="table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Status</th>
                <th>Priority</th>
                <th>City</th>
                <th>Industry</th>
                <th>Last Activity</th>
                <th>Subscription</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((account) => (
                <tr
                  key={account.id}
                  className="clickable-row"
                  onClick={() => navigate(`/accounts/${account.id}`)}
                >
                  <td>
                    <strong>{account.name}</strong>
                    <span>{account.website || account.phone || 'No direct profile yet'}</span>
                  </td>
                  <td><span className="pill pill-neutral">{account.status}</span></td>
                  <td><span className={`pill pill-${account.priority}`}>{account.priority}</span></td>
                  <td>{account.city || '—'}</td>
                  <td>{account.industry || '—'}</td>
                  <td>
                    <div className="table-cell-actions">
                      <span>{formatRelativeDate(account.last_activity_at)}</span>
                      {getApolloAccountUrls(account).accountUrl ? (
                        <button
                          type="button"
                          className="button button-secondary table-link-button"
                          onClick={(event) => {
                            event.stopPropagation();
                            window.open(getApolloAccountUrls(account).accountUrl!, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          <ExternalLink size={14} />
                          Apollo
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    {account.linked_tenant_id ? (
                      <button
                        type="button"
                        className="button button-secondary table-link-button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setEditingSubscriptionAccountId(account.id);
                        }}
                      >
                        <CreditCard size={14} />
                        Edit
                      </button>
                    ) : (
                      <span className="muted-copy">CRM-only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState title="No accounts yet" body="Create your first account or let lead conversion start populating this list." />
      )}
      </SectionCard>

      {editingSubscriptionAccountId ? (
        subscription.isLoading ? (
          <ModalPortal>
            <div className="apollo-modal-backdrop" role="presentation">
              <div className="apollo-modal" role="dialog" aria-modal="true" aria-label="Loading subscription">
                <div className="loading-row">Loading customer subscription...</div>
              </div>
            </div>
          </ModalPortal>
        ) : subscription.data ? (
          <EditSubscriptionOverridesDrawer
            accountId={editingSubscriptionAccountId}
            open
            onClose={() => setEditingSubscriptionAccountId(null)}
            initial={subscription.data}
          />
        ) : null
      ) : null}
    </div>
  );
}
